import { Analytics } from '@vercel/analytics/react'
import qs from 'query-string'
import { lazy, Suspense, useEffect } from 'react'
import { Outlet, ScrollRestoration, useNavigate } from 'react-router'
import { Toaster } from 'sonner'
import {
  buttonVariants,
  Portal,
  Spinner,
  DrawerCoordinatorProvider,
} from 'tailwind-ui'
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XCircleIcon,
} from '@heroicons/react/20/solid'
import { BrandKitStyles } from 'tailwind-ui/brand-kit/styles'
import { QueryParamProvider } from 'use-query-params'
import { ReactRouter6Adapter } from 'use-query-params/adapters/react-router-6'
import AlertProvider from '@/components/alerts'
import { UpdateAvailableBanner } from '@/components/update-available-banner'
import { UpdateProvider } from '@/components/update-context'
import { CallSessionProvider } from '@/components/calls/CallSessionProvider'
import { CallDialog } from '@/components/calls/CallDialog'
import { CallPill } from '@/components/calls/CallPill'
import { EngineeringAgentProvider } from '@/components/engineering-agent'
import AppNotifications from '@/components/ProfilePage/AppNotifications'
import { OnlineStatusProvider } from '@/connect-context/online-status'
import SegmentProvider from '@/connect-context/tracking/segment'
import useSaveLastRoute from '@/helpers/router'
import { useColorMode } from '@/hooks/use-color-mode'
import useUser from '@/hooks/useUser'
import OauthContext from '@/pages/oauth/context'
import FirebaseProvider from '@/utils/firebase/provider'
import { isInStandaloneMode } from '@/utils/helpers/pwa'
import { HistoryProvider } from '@/utils/router/history-provider'
import { SideNavProvider } from '@/utils/router/sidenav-provider-optimized'

const queryStringifyOptions = {
  searchStringToObject: qs.parse,
  objectToSearchString: qs.stringify,
}

const Offline = lazy(() => import('@/common/offline'))
const EmailVerification = lazy(() => import('@/common/email-verification'))

function RootLayout({ children = <Outlet /> }: { children?: React.ReactNode }) {
  useColorMode()

  const push = useNavigate()
  useSaveLastRoute()

  const user = useUser()
  const isStampede = Boolean(user?.email?.includes('@stampede.ai'))

  useEffect(() => {
    if (!isInStandaloneMode()) return
    const lastRoute = localStorage.getItem('last-visited-pathname')
    // If we have a saved route, redirect there instead of defaulting to, say, "/"
    if (lastRoute) {
      // Replace the current history entry so the user can still navigate back
      push(lastRoute, { replace: true })
    }
  }, [push])

  return (
    <HistoryProvider>
      <QueryParamProvider
        adapter={ReactRouter6Adapter}
        options={queryStringifyOptions}
      >
        <ScrollRestoration />
        <SegmentProvider>
          <OauthContext>
            <Analytics />

            <FirebaseProvider>
              <OnlineStatusProvider>
                <DrawerCoordinatorProvider debug={false}>
                  <AlertProvider>
                    <UpdateProvider>
                      <CallSessionProvider>
                      {'Notification' in window ? <AppNotifications /> : null}
                      {/* the phone call lives at the root: minimise it to a pill and keep moving */}
                      <CallDialog />
                      <CallPill />
                      <Portal>
                        {/* Toast spec — inverted-neutral surface keyed off the
                          tailwind-v4 palette. Light-mode page = dark toast
                          (bg-foreground / text-background), dark-mode page
                          = light toast. The inversion is automatic via the
                          theme tokens — no per-mode overrides. Success /
                          error / warning / info icons keep their semantic
                          colour so the row still reads at a glance against
                          the high-contrast bg.

                          Tweaks vs. previous:
                            - closeButton OFF — toasts are quick by design.
                            - duration 1800ms — was 4000, way too sticky.
                            - taller (h-12) and roomier padding for legibility. */}
                        <Toaster
                          theme="system"
                          position="bottom-center"
                          visibleToasts={3}
                          icons={{
                            loading: <Spinner size="sm" />,
                            success: <CheckCircleIcon className="h-4 w-4" />,
                            error: <XCircleIcon className="h-4 w-4" />,
                            warning: (
                              <ExclamationTriangleIcon className="h-4 w-4" />
                            ),
                            info: <InformationCircleIcon className="h-4 w-4" />,
                          }}
                          toastOptions={{
                            unstyled: true,
                            duration: 1800,
                            className: 'z-[9999]',
                            classNames: {
                              cancelButton:
                                'ml-auto rounded-lg self-center text-xs px-2 py-1 font-medium text-background/70 hover:text-background',
                              actionButton: buttonVariants({
                                variant: 'primary',
                                brandKit: true,
                                className: 'ml-auto rounded-lg self-center',
                              }),
                              success: 'fill-emerald-400',
                              error: 'fill-rose-400',
                              warning: 'fill-amber-400',
                              info: 'fill-sky-400',
                              description: 'text-xs opacity-80',
                              title:
                                'text-sm font-medium line-clamp-2 text-background',
                              // `new-ui-theme` className puts the toast back
                              // inside the theme scope — Sonner portals
                              // outside `.new-ui-theme`, so without this
                              // `var(--foreground)` resolves to undefined
                              // (transparent). Same trick we use on Sheet /
                              // Dialog / Popover popups.
                              toast:
                                'new-ui-theme z-[9999] flex h-12 min-w-80 w-full items-center gap-3 rounded-xl bg-foreground px-5 text-background shadow-lg ring-1 ring-foreground/10',
                            },
                          }}
                        />
                      </Portal>

                      {/* Root mount provides the legacy brand vars (fonts,
                        --brand-color, portal-loading bg) app-wide. `bridgeTokens`
                        is OFF here: injecting the v4 token bridge into a global
                        :root bleeds brand colours into every CRM control and
                        overrides the design-system tokens (washed text / brand
                        borders). Brand-tinted v4 tokens belong only in genuine
                        guest-facing previews (which mount their own, bridge on). */}
                      <BrandKitStyles
                        ignoreBody
                        bridgeTokens={false}
                        // Neutral CRM skeletons — the brand tint turned every
                        // admin loading screen pink (design law: venue/brand
                        // colour never themes CRM chrome).
                        brandSkeletons={false}
                        brand={{
                          backgroundImage: '',
                          interfaceColor: '#ff3a9b',
                          roundInputs: true,
                          background: 'white',
                          heading_font_family: 'Montserrat',
                          body_font_family: 'Montserrat',
                        }}
                      />
                      <SideNavProvider>
                        <EngineeringAgentProvider enabled={isStampede}>
                          {children}
                        </EngineeringAgentProvider>
                      </SideNavProvider>
                      <Suspense fallback={<></>}>
                        <Offline />
                      </Suspense>
                      <Suspense fallback={<></>}>
                        <EmailVerification />
                      </Suspense>
                      <UpdateAvailableBanner />
                    </CallSessionProvider>
                    </UpdateProvider>
                  </AlertProvider>
                </DrawerCoordinatorProvider>
              </OnlineStatusProvider>
            </FirebaseProvider>
          </OauthContext>
        </SegmentProvider>
      </QueryParamProvider>
    </HistoryProvider>
  )
}

export default RootLayout
